import { useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Plus, MoreVertical, HelpCircle, Download, Upload } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { GlossaryTab } from "@/components/glossary/glossary-tab";
import type { GlossaryCategoryRecord, GlossaryActions } from "@/components/glossary/glossary-tab";
import { GlossaryCategoryNav, GlossaryCategoryNavMobile } from "@/components/glossary/glossary-category-nav";
import { usePageTitle } from "@/hooks/use-page-title";

export default function AdminGlossary() {
  usePageTitle("Glossary");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categoryMgmtOpen, setCategoryMgmtOpen] = useState(false);
  const addGroupRef = useRef<(() => void) | null>(null);
  const actionsRef = useRef<GlossaryActions | null>(null);

  const categoriesQuery = useQuery<{ categories: GlossaryCategoryRecord[] }>({
    queryKey: ["/api/glossary/categories"],
  });

  const categories = useMemo(() =>
    (categoriesQuery.data?.categories || []).map((c) => ({ value: c.slug, label: c.label, groupCount: c.groupCount ?? 0 })),
    [categoriesQuery.data]
  );

  return (
    <div className="flex h-full">
      <GlossaryCategoryNav
        categories={categories}
        isLoading={categoriesQuery.isLoading}
        selected={categoryFilter}
        onSelect={setCategoryFilter}
        onManageCategories={() => setCategoryMgmtOpen(true)}
      />
      <div className="flex-1 min-w-0 overflow-auto">
        <div className="px-4 sm:px-6 pb-6 pt-4">
          <PageHeader
            title={
              <div className="flex items-center gap-2" data-testid="heading-glossary">
                <BookOpen className="h-6 w-6 text-primary" />
                <span>BETI Glossary</span>
              </div>
            }
            breadcrumb={[
              { label: "Administration" },
              { label: "BETI Glossary" },
            ]}
            actions={
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" data-testid="button-glossary-actions">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => actionsRef.current?.openInstructions()} data-testid="menu-csv-help">
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Instructions
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => actionsRef.current?.exportCsv()} disabled={!actionsRef.current?.canExport} data-testid="menu-export-csv">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => actionsRef.current?.importCsv()} data-testid="menu-import-csv">
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button onClick={() => addGroupRef.current?.()} data-testid="button-add-term">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Concept
                </Button>
              </div>
            }
          />
          <p className="text-muted-foreground mb-6">
            Teach BETI how shelter staff talk. Each <strong>concept</strong> represents something like "Behavior Score", and its <strong>entries</strong> are the specific codes or values staff use. Concepts are organized by <strong>category</strong> tags.
          </p>
          <GlossaryCategoryNavMobile
            categories={categories}
            isLoading={categoriesQuery.isLoading}
            selected={categoryFilter}
            onSelect={setCategoryFilter}
          />
          <GlossaryTab categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter} categories={categories} categoryMgmtOpen={categoryMgmtOpen} setCategoryMgmtOpen={setCategoryMgmtOpen} addGroupRef={addGroupRef} actionsRef={actionsRef} />
        </div>
      </div>
    </div>
  );
}
